/**
 * CIK 持仓与 Stock 接口补全逻辑公用方法
 * 供 useHoldingListTable、useHoldingChangeTable 共用
 */

import uniq from "lodash-es/uniq";
import type { AssetInfo } from "../types";
import type { CikAssetPosition } from "@/src/features/api/memberApi";
import type { Stock } from "@/src/features/api/openData";

/** 支持 getStocks 的 OpenData API 子集 */
export interface OpenDataApiForStocks {
  stocks: {
    getStocks(params: {
      ids: string[];
      limit: number;
      "enrich.enable": boolean;
      "enrich.with_trading_data"?: boolean;
      "enrich.with_rating"?: boolean;
    }): Promise<{
      data?: { data?: { rows?: unknown[] } };
    }>;
  };
}

/**
 * fetchStocksById 的补全选项，按需请求以节约资源
 */
export interface FetchStocksByIdOptions {
  /** 是否拉取行情数据（价格、涨跌幅等），列表表需要，变动表仅补全名称/logo 时可 false */
  withTradingData?: boolean;
  /** 是否拉取评级，列表表需要，变动表可 false */
  withRating?: boolean;
}

/**
 * 从 CikAssetPosition 取展示用 symbol（优先嵌套 asset.asset_symbol，兼容顶层 cik_symbol）
 */
export function getPositionSymbol(pos: CikAssetPosition): string {
  return pos.asset?.asset_symbol ?? pos.cik_symbol ?? "";
}

/**
 * 从 CikAssetPosition 取展示用 name（优先嵌套 asset.company_name，兼容顶层 name_of_issuer）
 */
export function getPositionName(pos: CikAssetPosition): string {
  return pos.name_of_issuer ?? "";
}

/**
 * 从 CikAssetPosition 取展示用 logo（优先嵌套 asset.asset_logo，兼容顶层 logo）
 */
export function getPositionLogo(pos: CikAssetPosition): string {
  return pos.asset?.asset_logo ?? pos.logo ?? "";
}

/**
 * 用 Stock 接口数据补充资产名称、logo（两表 Asset 列取值逻辑一致）
 */
export function enrichAssetFromStock(
  asset: AssetInfo,
  stock?: Stock,
): AssetInfo {
  if (!stock) {
    return asset;
  }
  return {
    ...asset,
    name: stock.short_name || stock.company_name || stock.name || asset.name,
    logo: stock.logo || asset.logo,
  };
}

/**
 * 根据 asset_id 列表调用 stocks.getStocks，返回 id -> Stock 映射
 * 出错时返回空对象，不抛错
 * @param options 补全选项，仅补全名称/logo 时传 { withTradingData: false, withRating: false } 节约资源
 */
export async function fetchStocksById(
  openDataApi: OpenDataApiForStocks | null | undefined,
  assetIds: string[],
  options: FetchStocksByIdOptions = {},
): Promise<Record<string, Stock>> {
  const ids = uniq(assetIds.filter(Boolean));
  if (!openDataApi || ids.length === 0) {
    return {};
  }

  const { withTradingData = true, withRating = true } = options;

  try {
    const res = await openDataApi.stocks.getStocks({
      ids,
      limit: ids.length,
      "enrich.enable": true,
      "enrich.with_trading_data": withTradingData,
      "enrich.with_rating": withRating,
    });
    const rows: Stock[] = Array.isArray(res?.data?.data?.rows)
      ? (res.data.data.rows as Stock[])
      : [];
    return rows.reduce<Record<string, Stock>>((map, stock) => {
      if (stock.id) {
        map[stock.id] = stock;
      }
      return map;
    }, {});
  } catch {
    return {};
  }
}
